/**
 * Render Tiptap document JSON as React elements.
 *
 * Deliberately not `dangerouslySetInnerHTML`: a shared report is rendered for
 * anyone holding the link, so the body is walked node by node and anything
 * unrecognised is dropped rather than injected.
 *
 * Every colour comes from a theme variable, so the same markup reads correctly
 * in the viewer's dark panels and on the light sheet of the share page, which
 * re-points those variables on its wrapper.
 */
import React from 'react';

import { detectDirection } from './detectDirection';

type Node = {
  type?: string;
  text?: string;
  attrs?: Record<string, any>;
  content?: Node[];
  marks?: { type: string; attrs?: Record<string, any> }[];
};

const ALIGN: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

function applyMarks(text: string, marks: Node['marks'], key: string) {
  let el: React.ReactNode = text;
  for (const mark of marks || []) {
    switch (mark.type) {
      case 'bold':
        el = <strong>{el}</strong>;
        break;
      case 'italic':
        el = <em>{el}</em>;
        break;
      case 'underline':
        el = <u>{el}</u>;
        break;
      case 'strike':
        el = <s>{el}</s>;
        break;
      case 'code':
        el = <code className="bg-secondary rounded px-1 text-[0.9em]">{el}</code>;
        break;
      default:
        break;
    }
  }
  return <React.Fragment key={key}>{el}</React.Fragment>;
}

export type MentionHandler = (refId: string, sliceIndex: number) => void;

/** Enough of a document's text for detectDirection to weigh, no more. */
function leadingText(node: Node, budget = { left: 200 }): string {
  if (budget.left <= 0) {
    return '';
  }
  if (node.type === 'text') {
    const value = (node.text || '').slice(0, budget.left);
    budget.left -= value.length;
    return value;
  }
  return (node.content || []).map(child => leadingText(child, budget)).join(' ');
}

function renderNodes(nodes: Node[] | undefined, onMention?: MentionHandler, prefix = 'n') {
  return (nodes || []).map((node, i) => renderNode(node, `${prefix}-${i}`, onMention));
}

function renderNode(
  node: Node,
  key: string,
  onMention?: MentionHandler
): React.ReactNode {
  const align = node.attrs?.textAlign ? ALIGN[node.attrs.textAlign] || '' : '';
  // Set by the editor's direction buttons; absent means inherit.
  const dir = node.attrs?.dir === 'rtl' || node.attrs?.dir === 'ltr' ? node.attrs.dir : undefined;

  switch (node.type) {
    case 'doc':
      return <React.Fragment key={key}>{renderNodes(node.content, onMention, key)}</React.Fragment>;

    case 'text':
      return applyMarks(node.text || '', node.marks, key);

    case 'paragraph':
      return (
        <p
          key={key}
          dir={dir}
          className={`mb-3 leading-relaxed ${align}`}
        >
          {renderNodes(node.content, onMention, key)}
        </p>
      );

    case 'heading': {
      const level = Math.min(Math.max(Number(node.attrs?.level) || 1, 1), 3);
      const Tag = (['h1', 'h2', 'h3'][level - 1] || 'h3') as any;
      const size = ['text-xl', 'text-lg', 'text-base'][level - 1];
      return (
        <Tag
          key={key}
          dir={dir}
          className={`mt-5 mb-2 font-semibold ${size} ${align}`}
        >
          {renderNodes(node.content, onMention, key)}
        </Tag>
      );
    }

    case 'bulletList':
      return (
        <ul
          key={key}
          dir={dir}
          className="mb-3 list-disc [padding-inline-start:1.25rem]"
        >
          {renderNodes(node.content, onMention, key)}
        </ul>
      );

    case 'orderedList':
      return (
        <ol
          key={key}
          dir={dir}
          className="mb-3 list-decimal [padding-inline-start:1.25rem]"
        >
          {renderNodes(node.content, onMention, key)}
        </ol>
      );

    case 'listItem':
      return <li key={key}>{renderNodes(node.content, onMention, key)}</li>;

    case 'blockquote':
      return (
        <blockquote
          key={key}
          dir={dir}
          className="border-primary/60 text-muted-foreground mb-3 italic [border-inline-start-width:2px] [padding-inline-start:0.75rem]"
        >
          {renderNodes(node.content, onMention, key)}
        </blockquote>
      );

    case 'codeBlock':
      return (
        <pre
          key={key}
          className="bg-secondary mb-3 overflow-x-auto rounded p-2 text-xs"
        >
          <code>{renderNodes(node.content, onMention, key)}</code>
        </pre>
      );

    case 'hardBreak':
      return <br key={key} />;

    case 'horizontalRule':
      return (
        <hr
          key={key}
          className="border-secondary my-4"
        />
      );

    case 'table':
      return (
        <div
          key={key}
          className="mb-3 overflow-x-auto"
        >
          <table className="border-secondary w-full border-collapse border text-left">
            <tbody>{renderNodes(node.content, onMention, key)}</tbody>
          </table>
        </div>
      );

    case 'tableRow':
      return <tr key={key}>{renderNodes(node.content, onMention, key)}</tr>;

    case 'tableHeader':
      return (
        <th
          key={key}
          className="border-secondary bg-secondary/40 border px-2 py-1 font-semibold"
          colSpan={node.attrs?.colspan || 1}
          rowSpan={node.attrs?.rowspan || 1}
        >
          {renderNodes(node.content, onMention, key)}
        </th>
      );

    case 'tableCell':
      return (
        <td
          key={key}
          className="border-secondary border px-2 py-1 align-top"
          colSpan={node.attrs?.colspan || 1}
          rowSpan={node.attrs?.rowspan || 1}
        >
          {renderNodes(node.content, onMention, key)}
        </td>
      );

    case 'sliceMention': {
      const sliceIndex = Number(node.attrs?.sliceIndex ?? 0);
      const refId = String(node.attrs?.refId || '');
      const label = node.attrs?.label || `Slice ${sliceIndex}`;
      return (
        <button
          key={key}
          type="button"
          onClick={() => onMention?.(refId, sliceIndex)}
          className="bg-primary/15 text-highlight border-primary/40 mx-0.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 align-baseline text-[0.85em] font-semibold leading-none"
        >
          <span aria-hidden="true">▣</span>
          {label}
        </button>
      );
    }

    default:
      return node.content ? (
        <React.Fragment key={key}>{renderNodes(node.content, onMention, key)}</React.Fragment>
      ) : null;
  }
}

export default function renderReportContent(doc: Node | null, onMention?: MentionHandler) {
  if (!doc || !doc.content?.length) {
    return null;
  }
  // Reports written before the editor had direction buttons carry no dir at
  // all. Setting it on the wrapper lets those inherit a sane direction, while
  // any block that does record one still overrides it — an explicit choice
  // always beats the guess.
  return <div dir={detectDirection(leadingText(doc))}>{renderNode(doc, 'root', onMention)}</div>;
}
